<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import { TRADEMARK_WORDMARK, IMPRINT } from "@config/constants/branding";
  import gameInfo from "@config/game_info.json";

  export let onContinue: () => void;
  export let onNewGame: () => void;
  export let onSettings: () => void;
  export let onHealthContentLegal: () => void;
  export let onEthicalMonetization: () => void;
  export let onCodex: () => void;
  export let hasSave = false;
  export let promos: Array<{ id: string; title: string; body: string; ctaLabel: string; ctaHref: string }> = [];

  function hideOnError(e: Event): void {
    const el = e.currentTarget as HTMLImageElement | null;
    if (el) el.style.display = "none";
  }
</script>

<BaseScene sceneId="title" ariaLabel="{gameInfo.product} title menu">
  <div class="title">
    <header class="title__header">
      <img
        src={gameInfo.wordmarkPath}
        alt={TRADEMARK_WORDMARK}
        class="title__wordmark"
        on:error={hideOnError}
      />
      <h1>{gameInfo.productSubtitle}</h1>
    </header>

    <nav class="title__menu" aria-label="Main menu">
      <section class="title__group" aria-label="Play">
        {#if hasSave}
          <button type="button" class="title__btn title__btn--primary" on:click={onContinue}>
            Continue
          </button>
        {/if}
        <button type="button" class="title__btn title__btn--primary" on:click={onNewGame}>
          {hasSave ? "New Game" : "Start"}
        </button>
      </section>

      <section class="title__group" aria-label="Customize">
        <button type="button" class="title__btn" on:click={onSettings}>Settings</button>
      </section>

      <section class="title__group" aria-label="About">
        <button type="button" class="title__btn" on:click={onHealthContentLegal}>
          Health, Content and Legal
        </button>
        <button type="button" class="title__btn" on:click={onEthicalMonetization}>
          About the Studio
        </button>
        <button type="button" class="title__btn" on:click={onCodex}>
          {gameInfo.codexLabel}
        </button>
      </section>
    </nav>

    <footer class="title__footer">
      <a
        href="https://{gameInfo.imprintDomain}"
        class="title__imprint"
        target="_blank"
        rel="noopener noreferrer"
      >
        {IMPRINT}
      </a>

      {#if promos.length > 0}
        <div class="title__promo-block">
          {#each promos.slice(0, 2) as promo (promo.id)}
            <a class="title__promo" href={promo.ctaHref} target="_blank" rel="noopener noreferrer">
              <strong>{promo.title}</strong>
              <p>{promo.body}</p>
              <span>{promo.ctaLabel}</span>
            </a>
          {/each}
        </div>
      {/if}
    </footer>
  </div>
</BaseScene>

<style>
  .title { max-width: 720px; margin: 0 auto; padding: 2rem 1rem; text-align: center; }
  .title__header { margin-bottom: 2rem; }
  .title__wordmark { max-width: 300px; width: 60%; }
  .title__header h1 { font-size: 2rem; color: var(--color-heading); margin: 0.5rem 0; }
  .title__menu { display: flex; flex-direction: column; gap: 1.5rem; margin-bottom: 2rem; }
  .title__group { display: flex; flex-direction: column; gap: 0.75rem; }
  .title__btn {
    min-width: 200px;
    min-height: 44px;
    padding: 0.75em 1.5em;
    font-size: 1.1rem;
    font-weight: 600;
    background: var(--color-surface);
    color: var(--color-accent);
    border: 2px solid var(--color-accent);
    border-radius: 0.5em;
    cursor: pointer;
    align-self: center;
  }
  .title__btn--primary { background: var(--color-accent); color: var(--color-on-accent); }
  .title__btn:focus-visible { outline: 3px solid var(--focus-ring); outline-offset: 2px; }
  .title__footer { border-top: 1px solid var(--color-divider); padding-top: 1rem; }
  .title__imprint {
    display: block;
    color: var(--color-muted);
    margin-bottom: 1rem;
    font-weight: 600;
    text-decoration: none;
  }
  .title__promo-block {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  .title__promo {
    display: block;
    padding: 1rem;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    text-decoration: none;
    color: var(--color-body);
    text-align: left;
  }
  .title__promo strong { display: block; color: var(--color-heading); margin-bottom: 0.25rem; }
  .title__promo p { font-size: 0.9rem; margin: 0 0 0.5rem 0; }
  .title__promo span { color: var(--color-accent); font-weight: 600; font-size: 0.9rem; }
</style>
